// Horarios de tienda (Fase 2): matemática de turnos compartida por el badge, el tope de duración
// y los avisos de solape del formulario. Es el mismo cálculo que OpeningHour en Ruby: minutos de
// la semana (0 = domingo 00:00), cierre <= apertura cruza medianoche, intervalo semiabierto
// [apertura, cierre), y "Abierto 24 horas" es la bandera allDay (opens y closes en 0).
// Doc: _docs/features/stores/store_hours.md.
export const DAY_MINUTES = 24 * 60
export const WEEK_MINUTES = 7 * DAY_MINUTES

export function crossesMidnight({ opens, closes, allDay }) {
  return !allDay && closes <= opens
}

export function durationMinutes(shift) {
  if (shift.allDay) return DAY_MINUTES
  return shift.closes <= shift.opens ? DAY_MINUTES - shift.opens + shift.closes : shift.closes - shift.opens
}

export function startsAt({ weekday, opens }) {
  return weekday * DAY_MINUTES + opens
}

export function endsAt(shift) {
  return startsAt(shift) + durationMinutes(shift)
}

export function covers(shift, weekMinute) {
  return mod(weekMinute - startsAt(shift), WEEK_MINUTES) < durationMinutes(shift)
}

export function overlaps(a, b) {
  return covers(a, startsAt(b)) || covers(b, startsAt(a))
}

// Empalme en el mismo día: uno abre justo cuando cierra el otro. La medianoche entre días no cuenta.
export function touches(a, b) {
  return a.weekday === b.weekday && (endsAt(a) === startsAt(b) || endsAt(b) === startsAt(a))
}

export function formatMinute(minute) {
  const hh = String(Math.floor(minute / 60) % 24).padStart(2, "0")
  const mm = String(minute % 60).padStart(2, "0")
  return `${hh}:${mm}`
}

// Un turno más largo que el tope es casi seguro un error de tipeo; todo el día es el checkbox.
export function shiftError(shift, maxMinutes) {
  if (shift.allDay || durationMinutes(shift) <= maxMinutes) return null
  return `Un turno no puede durar más de ${maxMinutes / 60} horas seguidas. Si abres todo el día, marca Abierto 24 horas.`
}

// Texto bajo "Cierre": confirma el día siguiente; vacío si cierra el mismo día o es de 24 horas.
export function badgeFor(shift, dayNames) {
  if (!crossesMidnight(shift)) return ""
  return `🌙 Cierra el ${dayNames[(shift.weekday + 1) % 7]} a las ${formatMinute(shift.closes)} (+1 día)`
}

// Aviso para el turno que choca con otro (el que abre dentro del otro, o el que empalma), o null.
export function conflictFor(a, b, dayNames) {
  if (touches(a, b)) {
    const later = startsAt(a) > startsAt(b) ? a : b
    return {
      shift: later,
      message: `El ${dayNames[later.weekday]} tiene un turno que abre a las ${formatMinute(later.opens)}, ` +
               "justo cuando cierra el anterior. Únelos en un solo turno."
    }
  }
  if (!overlaps(a, b)) return null
  const [later, earlier] = covers(a, startsAt(b)) ? [b, a] : [a, b]
  const opening = later.allDay
    ? `El ${dayNames[later.weekday]} abierto 24 horas`
    : `El horario de apertura del ${dayNames[later.weekday]} (${formatMinute(later.opens)})`
  return {
    shift: later,
    message: `${opening} se superpone con el cierre del ${dayNames[earlier.weekday]} (${formatMinute(earlier.closes)})`
  }
}

// Valores iniciales de un turno nuevo: una hora después del cierre del último turno vivo del día
// y cuatro horas de duración, así nace válido y sin aviso rojo; sin turnos, 09:00–18:00.
export const DEFAULT_SHIFT = { opens: 9 * 60, closes: 18 * 60 }
export const NEW_SHIFT_GAP = 60
export const NEW_SHIFT_LENGTH = 4 * 60

export function nextShiftDefaults(shifts) {
  if (shifts.length === 0) return { ...DEFAULT_SHIFT }
  const last = shifts.reduce((a, b) => (endsAt(b) > endsAt(a) ? b : a))
  const opens = mod(last.closes + NEW_SHIFT_GAP, DAY_MINUTES)
  return { opens, closes: mod(opens + NEW_SHIFT_LENGTH, DAY_MINUTES) }
}

const mod = (n, m) => ((n % m) + m) % m
